/**
 * PELU-08: ABM de paquetes de sesiones. La venta se hace desde el POS
 * (ítem 'package' del carrito) — Agenda solo define y consume.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from '../../shared/modals/Modal';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  TextInput,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure';
import { MasterFilterSheetModal } from '../../shared/filters/MasterFilterSheetModal';
import { OperationalConfirmModal } from '../../shared/modals/OperationalConfirmModal';
import { spacing } from '../../shared/theme/spacing';
import { ticketColors } from '../../shared/theme/ticketColors';
import { showSuccess } from '../../shared/ui/appToast';
import { VueltiDurmiendo } from '../../shared/ui/VueltiDurmiendo';
import { agenda, agendaFonts, agendaPaperSheet } from '../domain/agendaTheme';
import type { ServicePackage } from '../domain/servicePackageTypes';
import type { ServiceProductOption } from '../domain/types';
import {
  createServicePackage,
  listServicePackages,
  updateServicePackage,
} from '../infra/servicePackagesRepo';
import { listServiceProducts } from '../infra/appointmentsRepo';
import { formatMoney } from '../../shared/format/public-api';

type Mode = 'list' | 'form';

type Props = {
  visible: boolean;
  orgId?: string;
  /** Sucursal actual — filtra paquetes. */
  branchId?: string | null;
  isWide: boolean;
  onClose: () => void;
};

type FormState = {
  packageId: string | null;
  /** Nombre original al abrir edit — el header lo muestra aunque se edite el campo. */
  baselineName: string | null;
  name: string;
  price: string;
  sessionCount: string;
  validityDays: string;
  productIds: string[];
  sellOnPortal: boolean;
  active: boolean;
};

const emptyForm = (): FormState => ({
  packageId: null,
  baselineName: null,
  name: '',
  price: '',
  sessionCount: '4',
  validityDays: '180',
  productIds: [],
  sellOnPortal: false,
  active: true,
});

const formFromPackage = (pkg: ServicePackage): FormState => ({
  packageId: pkg.id,
  baselineName: pkg.name,
  name: pkg.name,
  price: String(pkg.price),
  sessionCount: String(pkg.sessionCount),
  validityDays: String(pkg.validityDays),
  productIds: pkg.items.map((i) => i.productId),
  sellOnPortal: pkg.sellOnPortal,
  active: pkg.active,
});

const parseMoney = (raw: string) => Number(raw.replace(',', '.'));

export function ServicePackagesModal(props: Props) {
  const insets = useSafeAreaInsets();
  const { height } = useWindowDimensions();
  const [mode, setMode] = useState<Mode>('list');
  const [packages, setPackages] = useState<ServicePackage[]>([]);
  const [services, setServices] = useState<ServiceProductOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [baseline, setBaseline] = useState<FormState>(emptyForm);
  const [servicePickerOpen, setServicePickerOpen] = useState(false);
  const [discardVisible, setDiscardVisible] = useState(false);
  const [freeConfirmVisible, setFreeConfirmVisible] = useState(false);

  const load = useCallback(async () => {
    if (!props.orgId) return;
    setLoading(true);
    try {
      const [pkgs, svcs] = await Promise.all([
        listServicePackages(true, props.branchId ?? null),
        listServiceProducts(props.orgId),
      ]);
      setPackages(pkgs);
      setServices(svcs);
    } catch (e) {
      presentUnexpectedFailure({
        module: 'appointments',
        op: 'service_packages.list',
        error: e,
        title: 'Paquetes',
        userMessage: 'No se pudieron cargar los paquetes.',
      });
      setPackages([]);
    } finally {
      setLoading(false);
    }
  }, [props.orgId, props.branchId]);

  useEffect(() => {
    if (!props.visible) return;
    setMode('list');
    setForm(emptyForm());
    setBaseline(emptyForm());
    setDiscardVisible(false);
    setFreeConfirmVisible(false);
    void load();
  }, [props.visible, load]);

  const serviceById = useMemo(() => {
    const map = new Map(services.map((s) => [s.id, s]));
    return map;
  }, [services]);

  const serviceOptions = useMemo(
    () =>
      services
        .filter((s) => !form.productIds.includes(s.id))
        .map((s) => ({
          id: s.id,
          name: s.name,
          subtitle: `${s.durationMinutes} min · ${formatMoney(s.price)}`,
        })),
    [services, form.productIds],
  );

  const openCreate = () => {
    const fresh = emptyForm();
    setForm(fresh);
    setBaseline(fresh);
    setMode('form');
  };

  const openEdit = (pkg: ServicePackage) => {
    const next = formFromPackage(pkg);
    setForm(next);
    setBaseline(next);
    setMode('form');
  };

  const isEdit = form.packageId != null;
  const parsedPrice = parseMoney(form.price);
  const parsedSessions = Number(form.sessionCount);
  const parsedValidity = Number(form.validityDays);

  // Validación live (patrón `missingBits` del form de turno): hint + CTA disabled.
  const missingBits: string[] = [];
  if (!form.name.trim()) missingBits.push('nombre');
  if (!form.price.trim() || !Number.isFinite(parsedPrice) || parsedPrice < 0) {
    missingBits.push('precio');
  }
  if (!form.sessionCount.trim() || !Number.isFinite(parsedSessions) || parsedSessions < 1) {
    missingBits.push('sesiones');
  }
  if (form.productIds.length === 0) missingBits.push('servicios');
  const canSubmit = missingBits.length === 0 && !saving;
  const canSubmitHint = missingBits.length > 0 ? `Falta: ${missingBits.join(' · ')}` : null;

  // Preview de pricing: $/sesión + ahorro vs suelto (anchor conservador:
  // el servicio cubierto más barato × sesiones).
  const pricingPreview = useMemo(() => {
    if (!form.price.trim() || !Number.isFinite(parsedPrice) || parsedPrice < 0) return null;
    if (!Number.isFinite(parsedSessions) || parsedSessions < 1) return null;
    const perSession = parsedPrice / parsedSessions;
    const coveredPrices = form.productIds
      .map((id) => serviceById.get(id)?.price)
      .filter((p): p is number => p != null && p > 0);
    let suffix = '';
    if (coveredPrices.length > 0) {
      const sueltoTotal = Math.min(...coveredPrices) * parsedSessions;
      const ahorro = sueltoTotal - parsedPrice;
      if (ahorro > 0) {
        suffix = ` · ahorrás ${formatMoney(ahorro)} vs suelto`;
      } else if (ahorro < 0) {
        suffix = ` · ojo: más caro que suelto`;
      }
    }
    return `${formatMoney(perSession)} por sesión${suffix}`;
  }, [form.price, parsedPrice, parsedSessions, form.productIds, serviceById]);

  const formDirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(baseline),
    [form, baseline],
  );

  /** Salida del form con guard — cubre Volver, Cerrar, Esc y tap en backdrop. */
  const requestClose = () => {
    if (saving) return;
    if (mode === 'form' && formDirty) {
      setDiscardVisible(true);
      return;
    }
    if (mode === 'form') {
      setMode('list');
      return;
    }
    props.onClose();
  };

  const confirmDiscard = () => {
    setDiscardVisible(false);
    setMode('list');
  };

  const doSave = async () => {
    const name = form.name.trim();
    const price = parsedPrice;
    const sessionCount = parsedSessions;
    const validityDays = parsedValidity;
    setSaving(true);
    try {
      if (form.packageId) {
        await updateServicePackage({
          packageId: form.packageId,
          name,
          price,
          sessionCount,
          productIds: form.productIds,
          validityDays: Number.isFinite(validityDays) ? validityDays : 180,
          sellOnPortal: form.sellOnPortal,
          active: form.active,
        });
        showSuccess('Paquete actualizado');
      } else {
        await createServicePackage({
          name,
          price,
          sessionCount,
          productIds: form.productIds,
          validityDays: Number.isFinite(validityDays) ? validityDays : 180,
          sellOnPortal: form.sellOnPortal,
          branchId: props.branchId ?? null,
        });
        showSuccess('Paquete creado');
      }
      setMode('list');
      await load();
    } catch (e) {
      presentUnexpectedFailure({
        module: 'appointments',
        op: 'service_packages.save',
        error: e,
        title: 'Paquetes',
        userMessage: 'No se pudo guardar el paquete.',
      });
    } finally {
      setSaving(false);
    }
  };

  const saveForm = () => {
    if (!canSubmit) return;
    // Foot-gun: $0 explícito pide confirmación — un paquete gratis es decisión,
    // no accidente de un campo vacío.
    if (parsedPrice === 0) {
      setFreeConfirmVisible(true);
      return;
    }
    void doSave();
  };

  if (!props.visible) return null;

  const sheetMax = Math.round(height * (props.isWide ? 0.9 : 0.88));
  const bottomPad = props.isWide ? spacing.sm : Math.max(insets.bottom, 24);
  const title =
    mode === 'form'
      ? isEdit
        ? `Editar · ${form.baselineName ?? 'paquete'}`
        : 'Nuevo paquete'
      : 'Paquetes';
  const subtitle =
    mode === 'list'
      ? 'Sesiones prepago para tus clientes'
      : isEdit
        ? 'Se vende desde el POS; los saldos ya vendidos siguen vigentes.'
        : 'Sesiones prepago — se vende desde el POS.';

  return (
    <>
      <Modal
        visible
        animationType={props.isWide ? 'fade' : 'slide'}
        transparent
        onRequestClose={requestClose}
        statusBarTranslucent
      >
        <View style={[styles.backdrop, props.isWide && styles.backdropWide]}>
          <Pressable
            style={StyleSheet.absoluteFill}
            onPress={requestClose}
            accessibilityRole="button"
            accessibilityLabel="Cerrar"
          />
          <View
            style={[
              styles.sheet,
              props.isWide && styles.sheetWide,
              { maxHeight: sheetMax, paddingBottom: bottomPad },
            ]}
          >
            {!props.isWide ? <View style={styles.grabber} /> : null}
            <View style={styles.header}>
              <View style={styles.headerTextWrap}>
                <Text style={styles.title} numberOfLines={1}>
                  {title}
                </Text>
                {subtitle ? (
                  <Text style={styles.subtitle} numberOfLines={2}>
                    {subtitle}
                  </Text>
                ) : null}
              </View>
              <TouchableOpacity
                onPress={requestClose}
                hitSlop={8}
                style={styles.closeBtn}
                disabled={saving}
                accessibilityRole="button"
                accessibilityLabel="Cerrar"
                accessibilityState={{ disabled: saving }}
              >
                <Text style={styles.close}>Cerrar</Text>
              </TouchableOpacity>
            </View>

            <ScrollView
              style={styles.scroll}
              contentContainerStyle={styles.body}
              keyboardShouldPersistTaps="handled"
            >
              {mode === 'list' ? (
                <>
                  <View style={styles.actionsRow}>
                    <TouchableOpacity
                      style={styles.primaryBtn}
                      onPress={openCreate}
                      accessibilityRole="button"
                      accessibilityLabel="Nuevo paquete"
                      testID="appointments.packages.new"
                    >
                      <Text style={styles.primaryBtnText}>Nuevo paquete</Text>
                    </TouchableOpacity>
                  </View>
                  {loading ? <Text style={styles.hint}>Cargando…</Text> : null}
                  {!loading && packages.length === 0 ? (
                    <View style={styles.emptyWrap}>
                      <VueltiDurmiendo size={72} />
                      <Text style={styles.emptyTitle}>Todavía no hay paquetes</Text>
                      <Text style={styles.hintCentered}>
                        Un paquete es plata por adelantado. Creá el primero.
                      </Text>
                    </View>
                  ) : null}
                  {packages.map((pkg) => (
                    <TouchableOpacity
                      key={pkg.id}
                      style={styles.card}
                      onPress={() => openEdit(pkg)}
                      accessibilityRole="button"
                      accessibilityLabel={`Editar ${pkg.name}`}
                    >
                      <View style={styles.cardTop}>
                        <Text style={styles.cardTitle} numberOfLines={1}>
                          {pkg.name}
                        </Text>
                        <Text style={[styles.badge, !pkg.active && styles.badgeOff]}>
                          {pkg.active ? 'Activo' : 'Inactivo'}
                        </Text>
                      </View>
                      <Text style={styles.cardMeta}>
                        {formatMoney(pkg.price)} · {pkg.sessionCount} sesiones
                        {pkg.sellOnPortal ? ' · portal' : ''}
                      </Text>
                      <Text style={styles.cardItems} numberOfLines={2}>
                        {pkg.items.map((i) => i.name || serviceById.get(i.productId)?.name || i.productId).join(' · ') ||
                          'Sin servicios'}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </>
              ) : null}

              {mode === 'form' ? (
                <>
                  <Text style={styles.label}>Nombre</Text>
                  <TextInput
                    style={styles.input}
                    value={form.name}
                    onChangeText={(name) => setForm((f) => ({ ...f, name }))}
                    placeholder="Ej. 4 cortes"
                    placeholderTextColor={agenda.ink3}
                    accessibilityLabel="Nombre del paquete"
                  />
                  <View style={styles.row2}>
                    <View style={styles.row2Cell}>
                      <Text style={styles.label}>Precio</Text>
                      <TextInput
                        style={styles.input}
                        value={form.price}
                        onChangeText={(price) => setForm((f) => ({ ...f, price }))}
                        keyboardType="decimal-pad"
                        placeholder="0"
                        placeholderTextColor={agenda.ink3}
                        accessibilityLabel="Precio del paquete"
                      />
                    </View>
                    <View style={styles.row2Cell}>
                      <Text style={styles.label}>Sesiones</Text>
                      <TextInput
                        style={styles.input}
                        value={form.sessionCount}
                        onChangeText={(sessionCount) => setForm((f) => ({ ...f, sessionCount }))}
                        keyboardType="number-pad"
                        placeholder="4"
                        placeholderTextColor={agenda.ink3}
                        accessibilityLabel="Cantidad de sesiones"
                      />
                    </View>
                  </View>
                  {pricingPreview ? (
                    <Text style={styles.pricePreview} accessibilityLiveRegion="polite">
                      {pricingPreview}
                    </Text>
                  ) : null}
                  <Text style={styles.label}>Vigencia (días)</Text>
                  <TextInput
                    style={styles.input}
                    value={form.validityDays}
                    onChangeText={(validityDays) => setForm((f) => ({ ...f, validityDays }))}
                    keyboardType="number-pad"
                    placeholder="180"
                    placeholderTextColor={agenda.ink3}
                    accessibilityLabel="Vigencia en días"
                  />
                  <Text style={styles.label}>Servicios cubiertos</Text>
                  {form.productIds.map((id) => {
                    const svc = serviceById.get(id);
                    return (
                      <View key={id} style={styles.chipRow}>
                        <Text style={styles.chipText} numberOfLines={1}>
                          {svc?.name ?? id}
                        </Text>
                        <TouchableOpacity
                          onPress={() =>
                            setForm((f) => ({
                              ...f,
                              productIds: f.productIds.filter((x) => x !== id),
                            }))
                          }
                          hitSlop={8}
                          style={styles.chipRemove}
                          accessibilityRole="button"
                          accessibilityLabel={`Quitar ${svc?.name ?? 'servicio'}`}
                        >
                          <Text style={styles.linkDanger}>Quitar</Text>
                        </TouchableOpacity>
                      </View>
                    );
                  })}
                  <TouchableOpacity
                    style={[
                      styles.secondaryBtn,
                      serviceOptions.length === 0 && styles.secondaryBtnDisabled,
                    ]}
                    onPress={() => setServicePickerOpen(true)}
                    disabled={serviceOptions.length === 0}
                    accessibilityRole="button"
                    accessibilityLabel="Sumar servicio"
                    accessibilityState={{ disabled: serviceOptions.length === 0 }}
                  >
                    <Text
                      style={[
                        styles.secondaryBtnText,
                        serviceOptions.length === 0 && styles.secondaryBtnTextDisabled,
                      ]}
                    >
                      Sumar servicio
                    </Text>
                  </TouchableOpacity>
                  {serviceOptions.length === 0 ? (
                    <Text style={styles.hint}>
                      {services.length === 0
                        ? 'No hay servicios cargados. Crealos en Catálogo primero.'
                        : 'Todos los servicios ya están en el paquete.'}
                    </Text>
                  ) : null}
                  <View style={styles.switchRow}>
                    <View style={styles.switchLabelWrap}>
                      <Text style={styles.labelInline}>Vender en el portal</Text>
                      <Text style={styles.hint}>El cliente puede comprarlo online.</Text>
                    </View>
                    <Switch
                      value={form.sellOnPortal}
                      onValueChange={(sellOnPortal) => setForm((f) => ({ ...f, sellOnPortal }))}
                      trackColor={{ false: agenda.borderControl, true: agenda.brand }}
                      thumbColor={agenda.surface}
                      accessibilityLabel="Vender en el portal"
                    />
                  </View>
                  {isEdit ? (
                    <View style={styles.switchRow}>
                      <View style={styles.switchLabelWrap}>
                        <Text style={styles.labelInline}>Activo</Text>
                        <Text style={styles.hint}>
                          Al desactivarlo no se vende más. Los saldos ya vendidos siguen vigentes.
                        </Text>
                      </View>
                      <Switch
                        value={form.active}
                        onValueChange={(active) => setForm((f) => ({ ...f, active }))}
                        trackColor={{ false: agenda.borderControl, true: agenda.brand }}
                        thumbColor={agenda.surface}
                        accessibilityLabel="Paquete activo"
                      />
                    </View>
                  ) : null}
                </>
              ) : null}
            </ScrollView>

            {mode === 'form' ? (
              <View style={styles.footer}>
                {!canSubmit && !saving && canSubmitHint ? (
                  <Text style={styles.canSubmitHint}>{canSubmitHint}</Text>
                ) : null}
                <View style={styles.actionsRow}>
                  <TouchableOpacity
                    style={styles.secondaryBtn}
                    onPress={requestClose}
                    disabled={saving}
                    accessibilityRole="button"
                    accessibilityLabel="Volver"
                    accessibilityState={{ disabled: saving }}
                  >
                    <Text style={styles.secondaryBtnText}>Volver</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[
                      styles.primaryBtn,
                      !canSubmit && styles.primaryBtnDisabled,
                    ]}
                    onPress={saveForm}
                    disabled={!canSubmit}
                    accessibilityRole="button"
                    accessibilityLabel={isEdit ? 'Guardar cambios' : 'Crear paquete'}
                    accessibilityState={{ disabled: !canSubmit }}
                    testID="appointments.packages.submit"
                  >
                    <Text
                      style={[
                        styles.primaryBtnText,
                        !canSubmit && styles.primaryBtnTextDisabled,
                      ]}
                    >
                      {saving ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Crear paquete'}
                    </Text>
                  </TouchableOpacity>
                </View>
              </View>
            ) : null}
          </View>
          <OperationalConfirmModal
            visible={discardVisible}
            presentation="overlay"
            title="Descartar cambios"
            kicker="Paquete"
            message="Se pierden los datos que escribiste."
            confirmLabel="Descartar"
            destructive
            onClose={() => setDiscardVisible(false)}
            onConfirm={confirmDiscard}
          />
          <OperationalConfirmModal
            visible={freeConfirmVisible}
            presentation="overlay"
            title="Paquete gratis"
            kicker="Paquete"
            entityName={form.name.trim() || undefined}
            message="Precio $0: el cliente no paga nada al comprarlo en el POS ni en el portal."
            confirmLabel="Crear gratis"
            onClose={() => setFreeConfirmVisible(false)}
            onConfirm={() => {
              setFreeConfirmVisible(false);
              void doSave();
            }}
          />
        </View>
      </Modal>

      <MasterFilterSheetModal
        visible={servicePickerOpen}
        title="Sumar servicio"
        options={serviceOptions}
        selectedId={null}
        appearance="ticket"
        onSelect={(id) => {
          if (!id) return;
          setForm((f) => ({ ...f, productIds: [...f.productIds, id] }));
          setServicePickerOpen(false);
        }}
        onClose={() => setServicePickerOpen(false)}
      />
    </>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: ticketColors.dim,
    justifyContent: 'flex-end',
  },
  backdropWide: {
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.lg,
  },
  sheet: {
    ...agendaPaperSheet,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    width: '100%',
  },
  sheetWide: {
    width: 560,
    maxWidth: '100%',
    borderRadius: agenda.paperRadius,
  },
  grabber: {
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: agenda.borderControl,
    alignSelf: 'center',
    marginTop: spacing.xs,
    marginBottom: spacing.xs,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    paddingHorizontal: spacing.md,
    paddingTop: spacing.md,
    paddingBottom: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: agenda.border,
  },
  headerTextWrap: { flex: 1 },
  title: {
    fontFamily: agendaFonts.sans,
    fontSize: 18,
    fontWeight: '700',
    color: agenda.ink,
  },
  subtitle: {
    marginTop: 2,
    fontSize: 13,
    color: agenda.ink2,
  },
  closeBtn: {
    minHeight: 44,
    justifyContent: 'center',
    paddingHorizontal: spacing.xs,
  },
  close: {
    color: agenda.brand,
    fontWeight: '600',
    fontSize: 15,
  },
  scroll: {
    flexGrow: 0,
    flexShrink: 1,
  },
  body: {
    padding: spacing.md,
    gap: 10,
  },
  footer: {
    paddingHorizontal: spacing.md,
    paddingTop: spacing.sm,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: agenda.border,
    gap: spacing.xs,
  },
  canSubmitHint: {
    fontSize: 12,
    color: ticketColors.warn,
    fontWeight: '600',
  },
  actionsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  primaryBtn: {
    backgroundColor: agenda.brand,
    borderRadius: agenda.radius,
    paddingHorizontal: spacing.lg,
    minHeight: 44,
    justifyContent: 'center',
  },
  primaryBtnDisabled: {
    backgroundColor: agenda.surface2,
  },
  primaryBtnText: {
    color: agenda.brandInk,
    fontWeight: '700',
    fontSize: 14,
  },
  primaryBtnTextDisabled: {
    color: agenda.ink3,
  },
  secondaryBtn: {
    borderWidth: 1,
    borderColor: agenda.borderControl,
    borderRadius: agenda.radius,
    paddingHorizontal: spacing.lg,
    minHeight: 44,
    justifyContent: 'center',
    backgroundColor: agenda.surface,
  },
  secondaryBtnDisabled: {
    backgroundColor: agenda.surface2,
    borderColor: agenda.border,
  },
  secondaryBtnText: {
    color: agenda.ink,
    fontWeight: '600',
    fontSize: 14,
  },
  secondaryBtnTextDisabled: {
    color: agenda.ink3,
  },
  emptyWrap: {
    alignItems: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.lg,
  },
  emptyTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: agenda.ink,
  },
  hintCentered: {
    fontSize: 13,
    color: agenda.ink2,
    textAlign: 'center',
  },
  card: {
    borderWidth: 1,
    borderColor: agenda.border,
    borderRadius: agenda.radius,
    padding: spacing.md,
    backgroundColor: agenda.surface2,
    gap: 4,
  },
  cardTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  cardTitle: {
    flex: 1,
    fontSize: 15,
    fontWeight: '700',
    color: agenda.ink,
  },
  cardMeta: {
    fontSize: 13,
    fontFamily: agendaFonts.mono,
    color: agenda.ink2,
  },
  cardItems: {
    fontSize: 12,
    color: agenda.ink3,
  },
  badge: {
    fontSize: 11,
    fontWeight: '700',
    color: agenda.confirmed.text,
    backgroundColor: agenda.confirmed.bg,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 999,
    overflow: 'hidden',
  },
  badgeOff: {
    color: agenda.ink2,
    backgroundColor: agenda.surface,
  },
  label: {
    fontSize: 13,
    fontWeight: '600',
    color: agenda.ink2,
    marginTop: 4,
  },
  labelInline: {
    fontSize: 14,
    fontWeight: '600',
    color: agenda.ink,
  },
  input: {
    borderWidth: 1,
    borderColor: agenda.borderControl,
    borderRadius: agenda.radius,
    paddingHorizontal: spacing.md,
    minHeight: 44,
    fontSize: 15,
    color: agenda.ink,
    backgroundColor: agenda.surface,
  },
  pricePreview: {
    fontSize: 13,
    fontFamily: agendaFonts.mono,
    color: agenda.brand,
    fontWeight: '600',
  },
  row2: {
    flexDirection: 'row',
    gap: 8,
  },
  row2Cell: { flex: 1 },
  chipRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  chipText: {
    flex: 1,
    color: agenda.ink,
    fontSize: 14,
  },
  chipRemove: {
    minHeight: 36,
    justifyContent: 'center',
    paddingHorizontal: spacing.xs,
  },
  linkDanger: {
    color: agenda.noShow.text,
    fontWeight: '600',
    fontSize: 13,
  },
  switchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4,
    minHeight: 44,
  },
  switchLabelWrap: { flex: 1, paddingRight: spacing.md },
  hint: {
    fontSize: 13,
    color: agenda.ink2,
  },
});
